// PreviewTester/utils/transformationSchema.js

/**
 * MINIMAL TRANSFORMATION STEP SCHEMA v2.0
 * 
 * Types:
 * - button: Single button
 * - button-group: Group of buttons
 * - style: CSS style transformations (highlight, bold, italic, underline, strikethrough)
 * - structural: HTML structure changes (table, cards, list, json)
 * - media: Media transformations (images)
 * - combined: Combined step with multiple phases
 */

// ============================================================
// SELECTOR SCHEMA
// ============================================================
// Minimal selector - just paths, no nested wrappers
const SelectorSchema = {
  start: "string",        // nth-child path
  end: "string",          // nth-child path
  loopSelector: "string"  // optional - only for loop-aware selectors
};

// ============================================================
// STEP SCHEMAS BY TYPE
// ============================================================

// Base fields for ALL steps
const BaseStepSchema = {
  id: "string",
  type: "string",         // button | button-group | style | structural | media | combined
  operation: "string",    // highlight | isButton | table | images | etc.
  selectors: [SelectorSchema],
  timestamp: "string"     // ISO date string
};

// BUTTON step
const ButtonStepSchema = {
  ...BaseStepSchema,
  type: "button",
  buttonText: "string"    // The button text
};

// BUTTON-GROUP step
const ButtonGroupStepSchema = {
  ...BaseStepSchema,
  type: "button-group",
  groupName: "string",
  buttons: ["string"]     // Array of button texts
};

// STYLE step
const StyleStepSchema = {
  ...BaseStepSchema,
  type: "style",
  fields: ["string"],      // Which fields are affected
  operations: ["string"],  // Array of style operations (highlight, bold, italic, underline, strikethrough)
  style_meta: {            // Metadata for style transformations
    full_row: "boolean",
    alignment: "string"    // 'left' | 'center' | 'right'
  }
};


// STRUCTURAL step
const StructuralStepSchema = {
  ...BaseStepSchema,
  type: "structural",
  fields: ["string"]      // Fields to display in the structure
};

// MEDIA step
const MediaStepSchema = {
  ...BaseStepSchema,
  type: "media",
  imageSize: {
    width: "number",
    height: "number"
  },  // optional - only if customized
  fields: ["string"]      // Which fields contain image URLs
};

// COMBINED step
const CombinedStepSchema = {
  ...BaseStepSchema,
  type: "combined",
  phases: {
    structural: {
      operations: [{ operation: "string", fields: ["string"] }]
    },
    media: {
      operations: [{ operation: "string", fields: ["string"], imageSize: { width: "number", height: "number" } }]
    },
    styles: {
      operations: [{ operation: "string", fields: ["string"] }]
    }
  }
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Check if a step is a button
 */
export const isButtonStep = (step) => step?.type === 'button';

/**
 * Check if a step is a button group
 */
export const isButtonGroupStep = (step) => step?.type === 'button-group';

/**
 * Check if a step is a style step
 */
export const isStyleStep = (step) => step?.type === 'style';

/**
 * Check if a step is a structural step
 */
export const isStructuralStep = (step) => step?.type === 'structural';

/**
 * Check if a step is a media step
 */
export const isMediaStep = (step) => step?.type === 'media';

/**
 * Check if a step is a combined step
 */
export const isCombinedStep = (step) => step?.type === 'combined';

/**
 * Check if a step has loop-aware selectors
 */
export const hasLoopSelector = (step) => {
  if (!step?.selectors) return false;
  return step.selectors.some(s => s.loopSelector);
};

/**
 * Get the number of selectors in a step
 */
export const getSelectorCount = (step) => {
  return step?.selectors?.length || 0;
};

/**
 * Clean a selector - remove nested wrappers
 */
export const cleanSelector = (selector) => {
  if (!selector) return null;

  // If it's already in the new format (just strings), return as-is
  if (typeof selector.start === 'string' && typeof selector.end === 'string') {
    return {
      start: selector.start,
      end: selector.end,
      ...(selector.loopSelector ? { loopSelector: selector.loopSelector } : {})
    };
  }

  // Migrate from old format
  return {
    start: selector.start?.elementPath || selector.start || '',
    end: selector.end?.elementPath || selector.end || '',
    ...(selector.loopSelector ? { loopSelector: selector.loopSelector } : {})
  };
};

/**
 * Clean selectors array
 */
export const cleanSelectors = (selectors) => {
  if (!selectors || !Array.isArray(selectors)) return [];
  return selectors.map(s => cleanSelector(s)).filter(s => s && s.start);
};

/**
 * Create a minimal button step
 */
export const createButtonStep = (buttonText, selectorPath, id = null) => {
  return {
    id: id || `btn_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    type: 'button',
    operation: 'isButton',
    selectors: [{
      start: selectorPath,
      end: selectorPath
    }],
    buttonText: buttonText,
    timestamp: new Date().toISOString()
  };
};

/**
 * Create a minimal button group step
 */
export const createButtonGroupStep = (groupName, buttons, selectorPath, id = null) => {
  return {
    id: id || `btngrp_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    type: 'button-group',
    operation: 'isButtonGroup',
    selectors: [{
      start: selectorPath,
      end: selectorPath
    }],
    groupName: groupName,
    buttons: buttons.map(b => typeof b === 'string' ? b : b.text || b),
    timestamp: new Date().toISOString()
  };
};

/**
 * Create a minimal style step - UPDATED to support multiple operations
 */
export const createStyleStep = (operations, fields, selectors, style_meta = {}, id = null) => {
  // Handle both single operation (string) and multiple operations (array)
  const opsArray = Array.isArray(operations) ? operations : (operations ? [operations] : []);
  
  return {
    id: id || `style_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    type: 'style',
    operation: opsArray.length === 1 ? opsArray[0] : 'combined', // For backward compatibility
    operations: opsArray,
    selectors: cleanSelectors(selectors),
    fields: fields || [],
    style_meta: {
      full_row: style_meta.full_row || false,
      alignment: style_meta.alignment || 'left',
      ...style_meta
    },
    timestamp: new Date().toISOString()
  };
};


/**
 * Check if a style step has multiple operations
 */
export const hasMultipleOperations = (step) => {
  if (!step || step.type !== 'style') return false;
  return step.operations && step.operations.length > 1;
};

/**
 * Check if a style step has a specific operation
 */
export const hasOperation = (step, operation) => {
  if (!step || step.type !== 'style') return false;
  if (step.operations && Array.isArray(step.operations)) {
    return step.operations.includes(operation);
  }
  return step.operation === operation;
};



/**
 * Create a minimal structural step
 */
export const createStructuralStep = (operation, fields, selectors, id = null) => {
  return {
    id: id || `struct_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    type: 'structural',
    operation: operation,
    selectors: cleanSelectors(selectors),
    fields: fields || [],
    timestamp: new Date().toISOString()
  };
};

/**
 * Create a minimal media step
 */
// PreviewTester/utils/transformationSchema.js


export const createMediaStep = (fields, selectors, imageSize = null, id = null) => {
  const step = {
    id: id || `media_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    type: 'media',
    operation: 'images',
    selectors: cleanSelectors(selectors),
    fields: fields || [],
    timestamp: new Date().toISOString()
  };

  if (imageSize) {
    // ✅ Store in imageSize object with all properties
    step.imageSize = {
      width: imageSize.width || 250,
      height: imageSize.height || 250,
      preserveAspectRatio: imageSize.preserveAspectRatio !== undefined ? imageSize.preserveAspectRatio : true
    };
    
    // ✅ Also store at root level for easy access and backward compatibility
    step.imageWidth = step.imageSize.width;
    step.imageHeight = step.imageSize.height;
    step.preserveAspectRatio = step.imageSize.preserveAspectRatio;
  }

  return step;
};

/**
 * Create a minimal combined step
 */
export const createCombinedStep = (phases, selectors, id = null) => {
  return {
    id: id || `combined_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    type: 'combined',
    operation: 'combined',
    selectors: cleanSelectors(selectors),
    phases: phases || {
      structural: { operations: [] },
      media: { operations: [] },
      styles: { operations: [] }
    },
    timestamp: new Date().toISOString()
  };
};

export default {
  isButtonStep,
  isButtonGroupStep,
  isStyleStep,
  isStructuralStep,
  isMediaStep,
  isCombinedStep,
  hasLoopSelector,
  getSelectorCount,
  cleanSelector,
  cleanSelectors,
  createButtonStep,
  createButtonGroupStep,
  createStyleStep,
  createStructuralStep,
  createMediaStep,
  createCombinedStep,
  hasMultipleOperations,
  hasOperation
};